import React, { useEffect, useState, useRef } from 'react';
import PageLayout from '@/components/PageLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Briefcase, GraduationCap, Award, User, Calendar, MapPin } from 'lucide-react';
import { motion, useInView, useAnimation } from 'framer-motion';
import DownloadButton from '@/components/custom/Download';
import { Helmet } from 'react-helmet';

// URL de votre CV (à remplacer par votre lien réel)
const CV_URL = 'https://drive.google.com/file/d/1GR1U0nVbZonuPCP3CkqndTkzp90w-ayy/view?usp=sharing';

const Resume = () => {
  const [loading, setLoading] = useState(true);
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  // Refs for scroll animations
  const s1Ref = useRef(null);


  // Animation Trigger
  const useAnimateOnScroll = (refs, animationClass) => {
    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("animate__animated", animationClass);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.3 }
      );

      refs.forEach((ref) => {
        if (ref.current) observer.observe(ref.current);
      });

      return () => observer.disconnect();
    }, [refs, animationClass]);
  };

  useAnimateOnScroll(
    [s1Ref],
    "animate__rollIn"
  );



  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [controls, isInView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const experienceItems = [

    {
      title: "Stagiaire Administrateur Système & Réseaux",
      company: "Conseil Congolais des Chargeurs ",
      period: "Janvier – Mars 2025",
      description: "IT Support & IT Infrastructure Management."
    },
    {
      title: "Stagiaire en Développement Web",
      company: "O'PRIME Inc",
      period: "Avril - Juin 2024",
      description: "Développement Web et UI/UX Design."
    }
   
  ];

  const educationItems = [
    {
      degree: "Diplome Ingénieur en Informatique",
      school: "IUI Douala",
      period: "2022 - Présent",
      description: "PFI Cible - Développeur Informatique."
    },
   
  ];

  const skills = [
    "Serverless & Microservices", "React", "Flutter", 
   "IA/ML", "CI/CD", "UI/UX Design",  "SMM","Web Analytics", "Automatisation",
  ];


  return (
    <>
    <Helmet>
    <title>Mopaossi | Services</title>
    <meta
      name="description"
      content="Développeur Frontend | No-code| Low-code | UI/UX | Agilité"
    />
    <meta property="og:title" content="Mopaossi | Développeur Créatif" />
    <meta
      property="og:description"
      content="Découvrer un créateur, digitalisation, tech, app, dev : Mes sujets préférés."
    />
    <meta property="og:type" content="website" />
    <link rel="canonical" href="https://cbenji.pages.dev" />
  </Helmet>
    <PageLayout
      title="Mopaossi | CV"
      description="Mon CV tout propre : Qu'est ce que vous me proposez ?"
    >
      <div className="max-w-7xl mx-auto px-4">
        {/* Hero Section with 3D Image */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-10 ">
          {/* Left: Text and Download Button */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 animate-gradient">
                Parcours Professionnel
              </span>
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              Développeur d'interfaces utilisateur modernes et intuitives avec React.
            </p>
            <div className="flex items-center gap-4">
              <DownloadButton 
                url={CV_URL} 
                filename="Mopaossi_CV.pdf" 
                label="Télécharger mon CV" 
              />
              <span className="text-sm text-gray-500">Format PDF (1.2 MB)</span>
            </div>
          </motion.div>
          
          {/* Right: 3D Animated Image */}
          <motion.div 
            className="w-full md:w-1/2 flex justify-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          >
            <div className="relative mx-auto">
              <img 
                src="https://hynmctzoemxttjsmvjyz.supabase.co/storage/v1/object/public/images//stage.png" 
                alt="Me 3D Illustration" 
                className="w-full h-full object-contain"
              />
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-32 h-4 bg-gradient-to-r from-blue-300 via-purple-300 to-blue-300 rounded-full blur-md opacity-40"></div>
            </div>
          </motion.div>
        </div>
        
        {/* Personal Info Card */}
        <motion.div
          className="mb-12 mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Card 
          ref={s1Ref}
          className="overflow-hidden border-none shadow-lg">
            <CardContent className="p-0">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 mt-12 p-6 text-white">
                <h2 className="text-2xl font-bold mb-2">Mopaossi</h2>
                <p className="text-white/80">Développeur Frontend & UI/UX Designer</p>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              
                <div className="flex items-center gap-3">
                  <MapPin className="text-blue-600" size={20} />
                  <span>Douala, Cameroun</span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="text-blue-600" size={20} />
                  <span>Disponible Août 2025 - Janvier 2026 </span>
                </div>
              
                <div className="flex items-center gap-3">
                  <MapPin className="text-blue-600" size={20} />
                  <span>Brazzaville, Congo</span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="text-blue-600" size={20} />
                  <span>Disponible  Août 2025 - Janvier 2026</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="text-blue-600" size={20} />
                  <span>A distance</span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="text-blue-600" size={20} />
                  <span>Temps partiel</span>
                </div>
              
              </div>
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Main Content */}
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {/* Experience Section */}
          <motion.div variants={itemVariants} className="md:col-span-2">
            <div className="flex items-center gap-2 mb-6">
              <Briefcase className="text-blue-600" />
              <h2 className="text-2xl font-bold">Expérience</h2>
            </div>
            <div className="space-y-6">
              {experienceItems.map((item, index) => (
                <motion.div 
                  key={index}
                  variants={itemVariants}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <h3 className="text-xl font-bold text-gray-800">{item.title}</h3>
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-blue-600 font-medium">{item.company}</p>
                    <span className="text-sm text-gray-500">{item.period}</span>
                  </div>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          {/* Education & Skills Section */}
          <motion.div variants={itemVariants} className="space-y-8">
            {/* Education */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <GraduationCap className="text-blue-600" />
                <h2 className="text-2xl font-bold">Formation</h2>
              </div>
              <div className="space-y-6">
                {educationItems.map((item, index) => (
                  <motion.div 
                    key={index}
                    variants={itemVariants}
                    className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                  >
                    <h3 className="text-xl font-bold text-gray-800">{item.degree}</h3>
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-blue-600 font-medium">{item.school}</p>
                      <span className="text-sm text-gray-500">{item.period}</span>
                    </div>
                    <p className="text-gray-600">{item.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
            
            {/* Skills */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <Award className="text-blue-600" />
                <h2 className="text-2xl font-bold">Compétences</h2>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex flex-wrap gap-2">
                  {skills.map((skill, index) => (
                    <motion.span
                      key={index}
                      variants={itemVariants}
                      className="px-3 py-1 bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 rounded-full text-sm font-medium"
                    >
                      {skill}
                    </motion.span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </PageLayout>
    </>
  );
};

export default Resume;